/**
 * Library › Quality › Live test cases (view `tests`): two tabs. "Test cases": every case with the layers that cover it
 * and its last result on dev, filtered by area and result, each opening its steps, expected results and latest run.
 * "Exploratory sessions": a charter per main flow with its sessions and findings. The content is English (Library
 * content); only the chrome is translated. Data: test-cases.yaml, test-results.yaml and exploratory.yaml.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import {
  caseLayers,
  filterCases,
  findingCounts,
  formatDate,
  latestResult,
  resultCounts,
  resultOf,
  type Charter,
  type Finding,
  type Severity,
  type TestCase,
  type TestLayer,
  type TestResultValue,
  type TestsData,
} from "./library";

const ENV = "dev";

const RESULT_LABEL: Record<TestResultValue, MessageKey> = {
  pass: "library.tests.result.pass",
  fail: "library.tests.result.fail",
  blocked: "library.tests.result.blocked",
  "not run": "library.tests.result.notRun",
};
const RESULT_TONE: Record<TestResultValue, string> = { pass: "ok", fail: "danger", blocked: "warn", "not run": "" };
const LAYER_LABEL: Record<TestLayer, MessageKey> = {
  unit: "library.tests.layer.unit",
  integration: "library.tests.layer.integration",
  e2e: "library.tests.layer.e2e",
  live: "library.tests.layer.live",
};
const SEVERITY_LABEL: Record<Severity, MessageKey> = {
  blocker: "library.sessions.severity.blocker",
  major: "library.sessions.severity.major",
  minor: "library.sessions.severity.minor",
  cosmetic: "library.sessions.severity.cosmetic",
};
const SEVERITY_TONE: Record<Severity, string> = { blocker: "danger", major: "danger", minor: "warn", cosmetic: "" };
const STATUS_LABEL: Record<Finding["status"], MessageKey> = {
  fixed: "library.sessions.status.fixed",
  "written up": "library.sessions.status.writtenUp",
  "not a bug": "library.sessions.status.notABug",
};

export function TestCasesView({ data }: { data: TestsData }) {
  const t = useT();
  const [tab, setTab] = useState<"cases" | "sessions">("cases");
  const tabs: ["cases" | "sessions", MessageKey][] = [["cases", "library.tests.tab.cases"], ["sessions", "library.tests.tab.sessions"]];
  return (
    <div className="stack">
      <div className="lib-tabs" role="tablist">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" id={`lt-tab-${key}`} aria-selected={tab === key} aria-controls={`lt-panel-${key}`} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>
            {t(label)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`lt-panel-${tab}`} aria-labelledby={`lt-tab-${tab}`}>
        {tab === "cases" ? <Cases data={data} /> : <Sessions charters={data.charters} />}
      </div>
    </div>
  );
}

function Pill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`lib-pill ${tone}`}>{children}</span>;
}

function Cases({ data }: { data: TestsData }) {
  const t = useT();
  const [area, setArea] = useState("");
  const [result, setResult] = useState<"" | TestResultValue>("");
  const [open, setOpen] = useState<string | null>(null);
  const rows = useMemo(() => filterCases(data, { area, result, environment: ENV }), [data, area, result]);
  const run = data.runs?.find((r) => r.environment === ENV);
  return (
    <div className="stack">
      <div className="lib-stats">
        {resultCounts(data, ENV).map(([r, n]) => (
          <div className="lib-stat" key={r}><b>{n}</b><span>{t(RESULT_LABEL[r])}</span></div>
        ))}
      </div>
      {run && <p className="lib-small">{t("library.tests.lastRun", { environment: run.environment, date: formatDate(run.date), api: run.api_build, dashboard: run.dashboard_build })}</p>}
      <div className="lib-filters" role="search">
        <label>
          {t("library.filter.area")}
          <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="live-cases-area">
            <option value="">{t("library.filter.allAreas")}</option>
            {data.areas.map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
        <label>
          {t("library.tests.filter.result")}
          <select value={result} onChange={(e) => setResult(e.target.value as "" | TestResultValue)} data-testid="live-cases-result">
            <option value="">{t("library.tests.filter.allResults")}</option>
            {(Object.keys(RESULT_LABEL) as TestResultValue[]).map((r) => <option key={r} value={r}>{t(RESULT_LABEL[r])}</option>)}
          </select>
        </label>
        <span className="lib-count" aria-live="polite">{t("library.filter.count", { shown: rows.length, total: data.cases.length })}</span>
      </div>
      <div className="lib-table-wrap tall">
        <table className="lib-table lt-table" data-testid="live-cases-table">
          <thead>
            <tr>
              <th>{t("library.tests.th.id")}</th>
              <th>{t("library.filter.area")}</th>
              <th>{t("library.tests.th.title")}</th>
              <th>{t("library.tests.th.layers")}</th>
              <th>{t("library.tests.th.result")}</th>
              <th>{t("library.tests.th.date")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const last = latestResult(data.results, c.id, ENV);
              const r = resultOf(data.results, c.id, ENV);
              const expanded = open === c.id;
              return (
                <Fragment key={c.id}>
                  <tr className={expanded ? "is-open" : undefined}>
                    <td className="nm">
                      <button type="button" className="lt-toggle" aria-expanded={expanded} aria-controls={`lt-${c.id}`} onClick={() => setOpen(expanded ? null : c.id)} title={t("library.tests.details", { id: c.id })}>
                        {expanded ? <ChevronDown size={14} aria-hidden /> : <DirIcon><ChevronRight size={14} aria-hidden /></DirIcon>}
                        {c.id}
                      </button>
                    </td>
                    <td>{c.area}</td>
                    <td className="wide">{c.title}</td>
                    <td>
                      <span className="lt-chips">{caseLayers(c).map((l) => <span key={l} className={`lib-kind lt-layer ${l}`}>{t(LAYER_LABEL[l])}</span>)}</span>
                    </td>
                    <td><Pill tone={RESULT_TONE[r]}>{t(RESULT_LABEL[r])}</Pill></td>
                    <td className="num">{last ? formatDate(last.date) : "—"}</td>
                  </tr>
                  {expanded && (
                    <tr className="lt-detail-row">
                      <td colSpan={6} id={`lt-${c.id}`}><CaseDetail c={c} data={data} /></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="lib-muted">{t("library.tests.noMatch")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CaseDetail({ c, data }: { c: TestCase; data: TestsData }) {
  const t = useT();
  const results = data.results.filter((r) => r.id === c.id);
  return (
    <div className="lt-detail" data-testid="live-case-detail">
      <div className="lt-detail-cols">
        <section>
          <h4>{t("library.tests.why")}</h4>
          <p>{c.why}</p>
          {c.preconditions.length > 0 && (
            <>
              <h4>{t("library.tests.preconditions")}</h4>
              <ul>{c.preconditions.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </>
          )}
          <h4>{t("library.tests.steps")}</h4>
          <ol>{c.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          <h4>{t("library.tests.expected")}</h4>
          <ul>{c.expected.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </section>
        <section>
          <h4>{t("library.tests.th.layers")}</h4>
          <ul className="lt-layers">
            {c.layers.map((l, i) => (
              <li key={i}><span className={`lib-kind lt-layer ${l.layer}`}>{t(LAYER_LABEL[l.layer])}</span> <code>{l.test}</code><div className="lib-small">{l.covers}</div></li>
            ))}
          </ul>
          <h4>{t("library.tests.automated")}</h4>
          <p>{c.automated && c.script ? <code>{c.script}</code> : t("library.tests.automatedNo")}</p>
          {c.blocked_on && (
            <>
              <h4>{t("library.tests.blockedOn")}</h4>
              <p>{c.blocked_on}</p>
            </>
          )}
          {results.map((r) => (
            <div key={r.environment} className="lt-result">
              <h4>{t("library.tests.latest", { environment: r.environment })}</h4>
              <p>
                <Pill tone={RESULT_TONE[r.result]}>{t(RESULT_LABEL[r.result])}</Pill> {formatDate(r.date)} · {t("library.tests.builds", { api: r.api_build, dashboard: r.dashboard_build })}
              </p>
              <p>{r.notes}</p>
              {r.metrics && <p className="lib-small"><code>{Object.entries(r.metrics).map(([k, v]) => `${k} ${v}`).join(" · ")}</code></p>}
              {r.link && <p><a href={r.link} target="_blank" rel="noreferrer">{r.link}</a></p>}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

function Sessions({ charters }: { charters: Charter[] }) {
  const t = useT();
  return (
    <div className="stack" data-testid="exploratory-sessions">
      {charters.map((ch) => (
        <section key={ch.id} className="lt-charter card">
          <header>
            <h3><span className="lib-muted">{ch.id}</span> {ch.area}</h3>
            <span className="lt-chips">
              {findingCounts(ch).map(([s, n]) => <Pill key={s} tone={SEVERITY_TONE[s]}>{n} · {t(SEVERITY_LABEL[s])}</Pill>)}
            </span>
          </header>
          <p><b>{t("library.sessions.mission")}</b> {ch.mission}</p>
          <p><b>{t("library.sessions.vary")}</b> {ch.vary.join(" · ")}</p>
          <p><b>{t("library.sessions.risks")}</b> {ch.risks.join(" · ")}</p>
          {ch.sessions.length === 0 && <p className="lib-muted">{t("library.sessions.none")}</p>}
          {ch.sessions.map((s, i) => (
            <div key={i} className="lt-session">
              <h4>{t("library.sessions.session", { date: formatDate(s.date), minutes: s.duration_min, api: s.build.api, dashboard: s.build.dashboard })}</h4>
              <p className="lib-small"><b>{t("library.sessions.tried")}</b></p>
              <ul className="lib-small">{s.tried.map((x, j) => <li key={j}>{x}</li>)}</ul>
              {s.findings.length === 0 ? (
                <p className="lib-muted">{t("library.sessions.noFindings")}</p>
              ) : (
                <ul className="lt-findings">
                  {s.findings.map((f, j) => <FindingItem key={j} f={f} />)}
                </ul>
              )}
              {s.notes && <p className="lib-small">{s.notes}</p>}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function FindingItem({ f }: { f: Finding }) {
  const t = useT();
  return (
    <li className="lt-finding">
      <div className="lt-finding-head">
        <b>{f.title}</b>
        <span className="lt-chips">
          <Pill tone={SEVERITY_TONE[f.severity]}>{t(SEVERITY_LABEL[f.severity])}</Pill>
          <Pill tone={f.status === "fixed" ? "ok" : "info"}>{t(STATUS_LABEL[f.status])}</Pill>
        </span>
      </div>
      <div className="lib-small">
        <b>{t("library.sessions.reproduce")}</b> {f.steps.join(" › ")}
      </div>
      <div className="lib-small"><b>{t("library.sessions.seen")}</b> {f.seen}</div>
      <div className="lib-small"><b>{t("library.sessions.expected")}</b> {f.expected}</div>
      {f.fix && <div className="lib-small"><b>{t("library.sessions.fix")}</b> {f.fix}</div>}
      {f.screenshot && <div className="lib-small lib-muted">{t("library.sessions.screenshot", { name: f.screenshot })}</div>}
      {f.link && <div className="lib-small"><a href={f.link} target="_blank" rel="noreferrer">{f.link}</a></div>}
    </li>
  );
}
